import React, { useState, useEffect } from "react";
import { Layout, Table, Select, Tag, Button, Row, Col, Input, Card, message, Space, Modal, Descriptions, Spin, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, ClearOutlined, FilterOutlined, EyeOutlined, PrinterOutlined, MoreOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";
import { getStatusColor } from "../../../utils/statusHelper";
import IssueNotePreviewModal from "../../../components/issuing/IssueNotePreviewModal";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";

const { Content } = Layout;
const { Option } = Select;

const IssueingList = () => {
    const navigate = useNavigate();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [detailVisible, setDetailVisible] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // PDF preview state
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewIinId, setPreviewIinId] = useState(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState(undefined);
    const [searchFilter, setSearchFilter] = useState("");

    // Applied Filters
    const [appliedStatus, setAppliedStatus] = useState(undefined);
    const [appliedSearch, setAppliedSearch] = useState("");

    useEffect(() => {
        fetchNotes();
    }, [appliedStatus, appliedSearch]);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const params = {};
            if (appliedStatus) params.iin_status = appliedStatus;
            if (appliedSearch) params.search = appliedSearch;

            const response = await api.get("/inventory/issuing/list.php", { params });
            if (response.data.status === "success") {
                setNotes(response.data.dataset || []);
            } else {
                message.error("Failed to fetch issue notes.");
            }
        } catch (error) {
            message.error("Failed to connect to issuing list endpoint.");
        } finally {
            setLoading(false);
        }
    };


    const handleOpenPdfPreview = (iinId) => {
        setPreviewIinId(iinId);
        setPreviewVisible(true);
    };

    const handleFilter = () => {
        setAppliedStatus(statusFilter);
        setAppliedSearch(searchFilter);
    };

    const handleClear = () => {
        setStatusFilter(undefined);
        setSearchFilter("");
        setAppliedStatus(undefined);
        setAppliedSearch("");
    };



    const columns = [
        {
            title: "Reference",
            dataIndex: "iin_ref",
            key: "iin_ref",
            render: (text) => <strong style={{ color: 'var(--fluid-primary)' }}>{text}</strong>
        },
        {
            title: "MR Ref",
            dataIndex: "mr_ref",
            key: "mr_ref",
        },
        {
            title: "Prepared By",
            dataIndex: "prepare_user_dname",
            key: "prepare_user_dname",
            render: (text) => text || "-"
        },
        {
            title: "Prepared Date",
            dataIndex: "prepare_dt",
            key: "prepare_dt",
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD hh:mm A") : "-"
        },
        {
            title: "Status",
            dataIndex: "iin_status",
            key: "iin_status",
            render: (status) => (
                <Tag color={getStatusColor(status)} style={{ fontWeight: 600 }}>
                    {status}
                </Tag>
            )
        },
        {
            title: "Total Items",
            dataIndex: "total_items",
            key: "total_items",
            align: 'center'
        },
        {
            title: "Total Qty",
            dataIndex: "total_qty",
            key: "total_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Collected By",
            dataIndex: "collect_user_dname",
            key: "collect_user_dname",
            render: (text) => text || "-"
        },
        {
            title: "Collected Date",
            dataIndex: "collected_dt",
            key: "collected_dt",
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD hh:mm A") : "-"
        },
        {
            title: "Action",
            key: "action",
            align: 'center',
            width: 80,
            render: (_, record) => {
                const items = [
                    {
                        key: "view",
                        label: "View Detail",
                        icon: <EyeOutlined />,
                        onClick: () => navigate(`/material-issue-note/view/${record.iin_id}`)
                    },
                    {
                        key: "print",
                        label: "Print",
                        icon: <PrinterOutlined />,
                        onClick: () => handleOpenPdfPreview(record.iin_id)
                    }
                ];
                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" icon={<MoreOutlined />} className="fluid-dots-btn" />
                    </Dropdown>
                );
            }
        }
    ];


    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'Material Issue Notes List' },
                ]} />
                <h1 className="fluid-page-title">
                    <EyeOutlined style={{ marginRight: 12 }} />Issue Note List
                </h1>
                
            </div>

            <Content className="fluid-content">
                <Card className="fluid-card">
                    <div style={{ marginBottom: 20 }}>
                        <Row gutter={[16, 16]} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={8} md={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select
                                    placeholder="Select Status"
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    style={{ width: "100%" }}
                                    allowClear
                                >
                                    <Option value="Preparing">Preparing</Option>
                                    <Option value="Ready">Ready</Option>
                                    <Option value="Issued">Issued</Option>
                                </Select>
                            </Col>

                            <Col xs={24} sm={8} md={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    placeholder="IIN Ref / MR Ref"
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    onPressEnter={handleFilter}
                                />
                            </Col>

                            <Col xs={24} sm={24} md={4}>
                                <div className="fluid-filter-actions" style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                    <Button
                                        type="primary"
                                        icon={<FilterOutlined />}
                                        onClick={handleFilter}
                                    >
                                        Filter
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClear}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={notes}
                        rowKey="iin_id"
                        loading={loading}
                        bordered
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            defaultPageSize: 10,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                        }}
                    />
                </Card>
            </Content>



            {/* PDF Preview Modal */}
            <IssueNotePreviewModal
                iinId={previewIinId}
                visible={previewVisible}
                onCancel={() => {
                    setPreviewVisible(false);
                    setPreviewIinId(null);
                }}
            />

            <Footerdiv />
        </Layout>
    );
};

export default IssueingList;
